/**
 * @fileoverview Custom hook for processing cost breakdown data from ecommerce statistics.
 * @module shared/hooks/useCostBreakdown
 */

import { useMemo } from 'react'
import { useRevenueVsCostsSeries } from '@/shared/hooks/dashboard'
import { useDashboardData } from '@/shared/hooks/dashboard/useDashboardData'
import { useFilter } from '@/shared/hooks/useFilter'

/**
 * Cost breakdown data structure
 */
export interface CostBreakdownData {
  /** Cost of goods sold */
  cogs: number
  /** Total advertising spend */
  adSpend: number
  /** Operating overhead (allocated indirect costs) when in OC mode */
  overhead?: number
  /** Net profit or Full Profit (when profitBasis==='OC') */
  profit: number
  /** Total revenue */
  totalRevenue: number
  /** Percentage breakdown (relative to revenue) */
  percentages: {
    cogs: number
    adSpend: number
    overhead?: number
    profit: number
  }
}

/**
 * Hook return type
 */
interface UseCostBreakdownReturn {
  /** Processed cost breakdown data or null if not available */
  data: CostBreakdownData | null
  /** Loading state indicator */
  isLoading: boolean
  /** Error message if processing failed */
  error: string | null
}

/**
 * Custom hook for processing cost breakdown data from ecommerce statistics.
 * Calculates COGS, ad spend, and profit with percentage breakdown.
 *
 * @returns Cost breakdown data with loading and error states
 * @example
 * ```tsx
 * const { data, isLoading, error } = useCostBreakdown();
 *
 * if (isLoading) return <Skeleton />;
 * if (error) return <ErrorDisplay message={error} />;
 * if (!data) return <EmptyState message="No cost data available" />;
 *
 * return <CostVsProfitDonut data={data} />;
 * ```
 */
export function useCostBreakdown(): UseCostBreakdownReturn {
  const { data: series, isLoading: loading, error } = useRevenueVsCostsSeries()
  const { salesTimeQuery } = useDashboardData()
  const { profitBasis } = useFilter()

  const data = useMemo((): CostBreakdownData | null => {
    if (!series || series.length === 0) {
      return null
    }

    // Aggregate totals from canonical series with full period fields
    const totals = series.reduce(
      (acc, point) => {
        const revenue = point.revenue ?? 0
        const cogs = point.cogs ?? 0
        const adSpend = point.adSpend ?? 0
        const cm2 = typeof point.profit === 'number' ? point.profit : revenue - (cogs + adSpend)

        acc.totalRevenue += revenue
        acc.cogs += cogs
        acc.adSpend += adSpend
        acc.profit += cm2
        return acc
      },
      { totalRevenue: 0, cogs: 0, adSpend: 0, profit: 0 }
    )

    // Operating overhead (allocated indirects) defaults to 0
    let overhead = 0

    // Adjust profit to Full Profit and compute overhead when requested
    if (profitBasis === 'OC') {
      const allocatedIndirectTotal =
        salesTimeQuery.data?.metadata?.totalAllocatedIndirectCosts ??
        salesTimeQuery.data?.indirectCosts?.total ??
        0
      overhead = allocatedIndirectTotal
      const ocTotalFromMeta = salesTimeQuery.data?.metadata?.totalAllocatedOperatingContribution
      totals.profit =
        typeof ocTotalFromMeta === 'number'
          ? ocTotalFromMeta
          : totals.profit - allocatedIndirectTotal
    }

    // Percentages relative to total revenue when available
    const percentages = {
      cogs: totals.totalRevenue > 0 ? (totals.cogs / totals.totalRevenue) * 100 : 0,
      adSpend: totals.totalRevenue > 0 ? (totals.adSpend / totals.totalRevenue) * 100 : 0,
      overhead: totals.totalRevenue > 0 ? (overhead / totals.totalRevenue) * 100 : 0,
      profit: totals.totalRevenue > 0 ? (totals.profit / totals.totalRevenue) * 100 : 0,
    }

    return {
      ...totals,
      overhead,
      percentages,
    }
  }, [series, salesTimeQuery.data, profitBasis])

  return { data, isLoading: loading, error }
}
